import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Lock, Mail, Stethoscope } from 'lucide-react';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useAuth } from '../../state/auth/AuthContext';
import { toast } from 'react-toastify';
import { getDashboardPath } from '../../utils/navigation';
import Logo from '../../components/Logo';

const SignIn = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  
  const { register, handleSubmit, formState: { errors } } = useForm({
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const onSubmit = async (data) => {
    setIsLoading(true);
    setErrorMsg('');
    try {
      const result = await login({ email: data.email, password: data.password });
      
      if (result && result.success && result.user) {
        // Role-based redirection with history replacement to prevent back navigation
        const userRole = result.user.role;
        const redirectPath = getDashboardPath(userRole);
        
        let welcomeMessage = 'Welcome back!';
        switch (userRole) {
          case 'Doctor':
            welcomeMessage = 'Welcome back, Doctor!';
            break;
          case 'Admin':
            welcomeMessage = 'Welcome back, Admin!';
            break;
          case 'Nurse':
            welcomeMessage = 'Welcome back, Nurse!';
            break;
          case 'Receptionist':
            welcomeMessage = 'Welcome back, Receptionist!';
            break;
        }
        
        toast.success(welcomeMessage);
        
        // Use replace to prevent back navigation to login page
        navigate(redirectPath, { replace: true });
        
        // Clear the browser history to prevent going back to auth pages
        if (typeof window !== 'undefined') {
          window.history.replaceState(null, '', redirectPath);
        }
      } 
    } catch (error) {
      toast.error(error.message || 'Sign in failed');
      setErrorMsg(error.message || 'Sign in failed');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#2D869C]/5 via-white to-[#9DC9D4]/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex justify-center items-center mb-4">
            <Logo size="lg" showText={false} />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-2">AvdCare HMS</h1>
          <p className="text-gray-600 font-medium">Welcome back! Please sign in to continue.</p>
        </div>

        {/* Sign In Form */}
        <div className="bg-white rounded-2xl border-2 border-gray-200 shadow-xl p-8">
          <div className="text-center mb-6">
            <h2 className="text-3xl font-bold text-gray-900">Sign In</h2>
            <p className="text-gray-600 font-medium mt-2">Access your healthcare dashboard</p>
          </div>

          {errorMsg && (
            <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl mb-6 font-semibold">
              {errorMsg}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Email Field */}
            <div>
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                icon={Mail}
                {...register('email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
                error={errors.email?.message}
              />
            </div>

            {/* Password Field */}
            <div>
              <div className="relative">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  icon={Lock}
                  {...register('password', { 
                    required: 'Password is required',
                    minLength: {
                      value: 6,
                      message: 'Password must be at least 6 characters'
                    }
                  })}
                  error={errors.password?.message}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-9 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password Link */}
            <div className="text-right">
              <Link 
                to="/forgot" 
                className="text-sm text-[#2D869C] hover:text-[#2D869C]/80 font-semibold transition-colors"
              >
                Forgot Password?
              </Link>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              className="w-full bg-[#2D869C] hover:bg-[#2D869C]/90 text-white font-bold py-3 px-4 rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex items-center justify-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  <span className="font-bold">Signing In...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Sign Up Link */}
          <div className="text-center mt-6">
            <p className="text-gray-600 text-sm font-medium">
              Don't have an account?{' '}
              <Link
                to="/signup"
                className="text-[#2D869C] hover:text-[#2D869C]/80 font-bold transition-colors"
              >
                Sign up here
              </Link>
            </p>
          </div>
        </div>

        {/* Demo Credentials */}
        <div className="mt-6 bg-[#2D869C]/5 border-2 border-[#2D869C]/20 rounded-xl p-5">
          <h3 className="text-sm font-bold text-gray-900 mb-3">Demo Credentials:</h3>
          <div className="space-y-2 text-xs text-gray-700">
            <div className="font-medium">
              <span className="font-bold text-[#2D869C]">Admin:</span> admin@hospital.com / AdminPass123!
            </div>
            <div className="font-medium">
              <span className="font-bold text-[#2D869C]">Doctor:</span> avddr@hospital.com / SecurePass123!
            </div>
            <div className="font-medium">
              <span className="font-bold text-[#2D869C]">Receptionist:</span> receptionist@hospital.com / SecurePass123!
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-8">
          <p className="text-xs text-gray-500 font-medium">
            © 2026 SmartCare HMS. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SignIn;
